/**
 * SIGNING IN AGAIN BEFORE A PAYOUT-METHOD WRITE - the one re-authentication in the cabinet.
 *
 * `updatePayoutMethod` is the API's one re-auth-gated operation (D11, authors-api.yaml's
 * `updatePayoutMethod`): the server refuses a token whose sign-in is older than its window with
 * 403 `REAUTH_REQUIRED` (`src/modules/authors/services/reauth.gate.ts`, five minutes), and "The
 * client answers this by re-authenticating and retrying". What the browser can do about it depends
 * on HOW the session signs in, and this file is where that is decided:
 *
 *   Apple or Google   a popup with the SAME provider - a fresh sign-in, so a fresh `auth_time` - and
 *                     then ONE forced token refresh, so the retry carries the new token and not the
 *                     cached one. The screen then retries the write once, and only once: a second
 *                     `REAUTH_REQUIRED` is shown, never answered with another popup (a loop).
 *   e-mail link only  NO POPUP EXISTS. A magic link is an e-mail round trip that leaves the page, so
 *                     the honest path is: sign out here, ask `/sign-in` for a fresh link, open it,
 *                     come back and type the address again. Nothing typed is kept across it - a
 *                     payout address in browser storage is the thing to avoid (C1-01). The link
 *                     comes back HERE: the screen signs out to `/sign-in?next=/payments/method`, and
 *                     the adapter sends the link to that same address (`magicLinkSettings` in
 *                     firebase-auth-client.ts, wave 25c), whose `next` the sign-in then follows.
 *
 * ★ WHICH PROVIDER IS READ OFF `AuthIdentity.providerLabels` - rendered everywhere else, and read
 * here only (the port says so). The one deliberate exception, and it is narrow: the labels are the
 * adapter's own table (`features/auth/firebase/firebase-auth-client.ts`, `PROVIDER_LABELS`: Apple,
 * Google, "E-mail link"; the fixture client uses the same words), and a label this file does not
 * know simply offers no popup. A provider id on the port would be the cleaner input - reported, not
 * added, since the port is the sign-in track's file.
 *
 * ★ THE ACCOUNT MUST NOT CHANGE. A popup lets the person pick ANY Google account; picking another
 * one signs the page in as somebody else. So the uid is compared before and after, and a changed
 * one is `switched-account` - the retry is never sent, because it would write this address onto
 * the other account's payouts. The same check guards G30's link (`features/auth/session/
 * link-anonymous.ts`).
 *
 * NOTHING IS AWAITED BEFORE THE POPUP OPENS: `signInWithPopup` is the first asynchronous call, so
 * the window still opens inside the click that asked for it (the adapter's own rule).
 */
import {
  PopupBlockedError,
  PopupCancelledError,
  ProviderUnreachableError,
} from '../../features/auth/firebase/auth-port';
import type {
  AuthClient,
  AuthIdentity,
  PopupProvider,
} from '../../features/auth/firebase/auth-port';

/** How this session can prove, again, that it is who it says. */
export type ReauthMethod =
  | { readonly kind: 'popup'; readonly provider: PopupProvider; readonly label: string }
  /** Signs in by e-mail link only: sign out, sign in again, come back. */
  | { readonly kind: 'email-link' }
  /** No session at all, or none this screen can read - sign in again by any door. */
  | { readonly kind: 'sign-in-again' };

/** The adapter's words for the two popup providers - see the header on why they are read. */
const POPUP_PROVIDER_BY_LABEL: Readonly<Record<string, PopupProvider>> = {
  Google: 'google',
  Apple: 'apple',
};

const EMAIL_LINK_LABEL = 'E-mail link';

export function reauthMethodFor(identity: AuthIdentity | null): ReauthMethod {
  if (identity === null || identity.isAnonymous) {
    return { kind: 'sign-in-again' };
  }
  for (const label of identity.providerLabels) {
    const provider = POPUP_PROVIDER_BY_LABEL[label];
    if (provider !== undefined) {
      return { kind: 'popup', provider, label };
    }
  }
  return identity.providerLabels.includes(EMAIL_LINK_LABEL)
    ? { kind: 'email-link' }
    : { kind: 'sign-in-again' };
}

export type ReauthOutcome =
  /** Signed in again as the same person, and the token refreshed. Retry the write once. */
  | { readonly kind: 'confirmed' }
  /** The person closed the window. Not an error: the form stays as it was. */
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'blocked' }
  | { readonly kind: 'unreachable' }
  /** A different account was chosen. The page is now signed in as it; nothing is retried. */
  | { readonly kind: 'switched-account' }
  | { readonly kind: 'failed' };

export async function reauthenticateWithPopup(
  client: AuthClient,
  provider: PopupProvider,
): Promise<ReauthOutcome> {
  const before = client.currentSession()?.identity.uid ?? null;
  if (before === null) {
    return { kind: 'failed' };
  }
  try {
    const session = await client.signInWithPopup(provider);
    if (session.identity.uid !== before) {
      return { kind: 'switched-account' };
    }
    // The ONE forced refresh: the SDK now caches a token minted by this sign-in, and the retry's
    // ordinary token read picks it up.
    await session.getIdToken(true);
    return { kind: 'confirmed' };
  } catch (error) {
    if (error instanceof PopupCancelledError) {
      return { kind: 'cancelled' };
    }
    if (error instanceof PopupBlockedError) {
      return { kind: 'blocked' };
    }
    if (error instanceof ProviderUnreachableError) {
      return { kind: 'unreachable' };
    }
    return { kind: 'failed' };
  }
}
